import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';

import LoadThemeComponent from '../../../../base/LoadThemeComponent';

import InfoCircleTooltip from 'components/common/InfoCircleTooltip';
import {
    RANGE_FROM,
    PRIMARY_NUMBERS,
    RANGE_TO,
    MIN_NUMBERS_IN_POOL,
    MAX_NUMBERS_IN_POOL
} from 'components/play/helpers/linesHelper';
import useLinesHelper from 'components/play/helpers/useLinesHelper';
import { setNumbersToPlay } from 'components/play/redux/kenoStoreSlice';
import { RootState } from 'components/play/redux/store';
import { setMultiplier } from 'components/play/redux/storeSlice';
import { rangeArray } from 'utils/helpers';

export default function StakeAndNumberPanel() {
    const dispatch = useDispatch();
    const { numbersToPlay } = useSelector((state: RootState) => state.kenoPlayStore);
    const { t } = useTranslation();
    const { lottery, lines, ranges, numbersPerLine, setLines } = useLinesHelper();

    useEffect(() => {
        if (numbersToPlay > 0 && lines.length > 0) {
            // trim line length to numbersToPlay
            setLines(lines.map((line) => line.map((numbers) => numbers.slice(0, numbersToPlay))));
        }
    }, [numbersToPlay]);

    useEffect(() => {
        dispatch(setNumbersToPlay(numbersPerLine[PRIMARY_NUMBERS][MAX_NUMBERS_IN_POOL]));
    }, []);

    const updateNumbersToPlay = (change: number) => {
        const newCount = Math.max(1, Math.min(numbersPerLine[PRIMARY_NUMBERS], numbersToPlay + change));
        dispatch(setNumbersToPlay(newCount));
    };

    const [currentMultiplier, setCurrentMultiplier] = useState(lottery.multipliers[0]?.multiplier || 1);

    const handleMultiplierChange = (change: number) => {
        const multipliers = lottery.multipliers.map((item) => item.multiplier);
        const currentIndex = multipliers.indexOf(currentMultiplier);
        const newIndex = Math.max(0, Math.min(multipliers.length - 1, currentIndex + change));
        const newMultiplier = multipliers[newIndex];
        setCurrentMultiplier(newMultiplier);
        dispatch(setMultiplier(newMultiplier));
    };

    const baseView = () => (
        <div className="card options-card card-choose-stake-and-numbers">
            <div className="card-body">
                <div className="row">
                    <div className="col-6 pl-0 d-flex justify-content-between flex-column">
                        <p>
                            <strong>{t('playPicker.keno.numbersPerLineTitle')}</strong>
                        </p>
                        <p className="fs-15">
                            {t('playPicker.keno.numbersPerLineInfo', {
                                minNumber: numbersPerLine[PRIMARY_NUMBERS][MIN_NUMBERS_IN_POOL],
                                maxNumber: numbersPerLine[PRIMARY_NUMBERS][MAX_NUMBERS_IN_POOL],
                                from: ranges[PRIMARY_NUMBERS][RANGE_FROM],
                                to: ranges[PRIMARY_NUMBERS][RANGE_TO],
                            })}
                        </p>

                        <select
                            className="form-control multi-ticket-count"
                            id="numbers-size-chooser"
                            name="numbers-size-chooser"
                            onChange={(e) => dispatch(setNumbersToPlay(Number(e.target.value)))}
                        >
                            {rangeArray(
                                numbersPerLine[PRIMARY_NUMBERS][MIN_NUMBERS_IN_POOL],
                                numbersPerLine[PRIMARY_NUMBERS][MAX_NUMBERS_IN_POOL]
                            )
                                .reverse()
                                .map((i) => (
                                    <option key={i} value={i}>
                                        {i} {t('playPicker.keno.number', { count: i })}
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div className="col-6 pr-0 d-flex justify-content-between flex-column">
                        <p>
                            <strong>{t('playPicker.keno.multiplierTitle')}</strong>
                            <InfoCircleTooltip content={t('playPicker.keno.multiplierTitleHelp')} />
                        </p>
                        <p className="fs-15">{t('playPicker.keno.multiplierInfo')}</p>
                        <select
                            className="form-control multi-ticket-count"
                            name="multiplier"
                            onChange={(e) => dispatch(setMultiplier(Number(e.target.value)))}
                        >
                            {lottery.multipliers.map((item) => (
                                <option key={item.id} value={item.multiplier}>
                                    {item.multiplier}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );

    const defaultView = () => (
        <div className="row d-flex justify-content-between g-0">
            <div className="col-6">
                <div className="card options-card d-md-flex h-100">
                    <div className="card-header">{t('playPicker.keno.howManyNumbers')}</div>
                    <div className="card-body">
                        <div className="col-12 d-flex align-items-center justify-content-center">
                            <button
                                type="button"
                                className="btn button-draw-count d-flex justify-content-center align-items-end"
                                onClick={() => updateNumbersToPlay(-1)}
                            >
                                -
                            </button>
                            <span className="draw-count">{numbersToPlay}</span>
                            <button
                                type="button"
                                className="btn button-draw-count d-flex justify-content-center align-items-end"
                                onClick={() => updateNumbersToPlay(1)}
                            >
                                +
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-6">
                <div className="card options-card d-md-flex h-100">
                    <div className="card-header">{t('playPicker.keno.chooseYourStake')}</div>
                    <div className="card-body">
                        <div className="col-12 d-flex align-items-center justify-content-center">
                            <button
                                type="button"
                                className="btn button-draw-count d-flex justify-content-center align-items-end"
                                onClick={() => handleMultiplierChange(-1)}
                            >
                                -
                            </button>
                            <span className="draw-count">{currentMultiplier}</span>
                            <button
                                type="button"
                                className="btn button-draw-count d-flex justify-content-center align-items-end"
                                onClick={() => handleMultiplierChange(1)}
                            >
                                +
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}
